import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Minus, Check, ChevronLeft, ChevronRight, Package } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useQuote } from '../context/QuoteContext';
import { productService } from '../services/api';
import { toast } from '../hooks/use-toast';

// Product image component with fallback
const ProductImage = ({ src, alt, className, children }) => {
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  // Set a timeout to show fallback if image doesn't load within 3 seconds
  useEffect(() => {
    if (src && isLoading) {
      const timer = setTimeout(() => {
        if (isLoading) {
          setHasError(true);
        }
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [src, isLoading]);
  
  const getColorFromName = (name) => {
    const colors = [
      'from-pink-400 to-pink-600',
      'from-blue-400 to-blue-600',
      'from-green-400 to-green-600',
      'from-purple-400 to-purple-600',
      'from-orange-400 to-orange-600',
      'from-teal-400 to-teal-600',
      'from-red-400 to-red-600',
      'from-indigo-400 to-indigo-600',
    ];
    const hash = (name || '').split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  if (hasError || !src) {
    return (
      <div className={`${className} bg-gradient-to-br ${getColorFromName(alt)} flex items-center justify-center relative`}>
        <div className="text-center text-white p-4">
          <Package className="h-16 w-16 mx-auto mb-2 opacity-80" />
          <p className="text-sm font-medium opacity-90 line-clamp-2">{alt}</p>
        </div>
        {children}
      </div>
    );
  }

  return (
    <div className={`${className} relative bg-gray-100`}>
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-200 to-gray-300">
          <div className="animate-pulse text-gray-400">
            <Package className="h-12 w-12" />
          </div>
        </div>
      )}
      <img
        src={src}
        alt={alt}
        className={`w-full h-full object-cover ${isLoading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
        onError={() => setHasError(true)}
        onLoad={() => setIsLoading(false)}
      />
      {children}
    </div>
  );
};

const ProductDetail = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { addToQuote } = useQuote();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(50);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const data = await productService.getProductByCode(productId);
        setProduct(data);
        
        // Fetch related products
        const allProducts = await productService.getAllProducts(data.category);
        const related = allProducts
          .filter(p => p.product_code !== productId)
          .slice(0, 4);
        setRelatedProducts(related);
      } catch (error) {
        console.error('Error loading product:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [productId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600 mx-auto mb-4"></div>
          <p className="text-lg text-gray-600">Loading product...</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Product not found</h2>
          <Link to="/products/all">
            <Button>Browse All Products</Button>
          </Link>
        </div>
      </div>
    );
  }

  // Get all images for the product - prefer local images
  const getImageUrl = (url) => {
    if (product.local_image) {
      return `https://procloudpromo.com${product.local_image}`;
    }
    return url;
  };
  
  const productImages = product.image_urls && product.image_urls.length > 0 
    ? product.image_urls.map(getImageUrl)
    : [getImageUrl(product.image_url)];

  const handlePrevImage = () => {
    setSelectedImageIndex((prev) => 
      prev === 0 ? productImages.length - 1 : prev - 1
    );
  };

  const handleNextImage = () => {
    setSelectedImageIndex((prev) => 
      prev === productImages.length - 1 ? 0 : prev + 1
    );
  };

  const handleAddToQuote = () => {
    addToQuote(product, quantity);
    toast({
      title: 'Added to Quote Request',
      description: `${product.name} (${quantity} units) added to your quote.`,
    });
  };

  const handleRequestQuoteNow = () => {
    addToQuote(product, quantity);
    navigate('/quote-cart');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Products
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          {/* Product Images Gallery */}
          <div>
            <Card>
              <CardContent className="p-6">
                {/* Main Image */}
                <div className="relative mb-4">
                  <ProductImage
                    src={productImages[selectedImageIndex]}
                    alt={product.name}
                    className="aspect-square rounded-lg overflow-hidden"
                  >
                    {product.is_new && (
                      <Badge className="absolute top-4 left-4 bg-pink-600 text-white">
                        New Product
                      </Badge>
                    )}
                    
                    {/* Navigation Arrows */}
                    {productImages.length > 1 && (
                      <>
                        <button
                          onClick={handlePrevImage}
                          className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg transition-all"
                        >
                          <ChevronLeft className="h-6 w-6 text-gray-800" />
                        </button>
                        <button
                          onClick={handleNextImage}
                          className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg transition-all"
                        >
                          <ChevronRight className="h-6 w-6 text-gray-800" />
                        </button>
                      </>
                    )}
                  </ProductImage>
                </div>
                
                {/* Thumbnail Images */}
                {productImages.length > 1 && (
                  <div className="grid grid-cols-4 gap-2">
                    {productImages.map((img, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedImageIndex(index)}
                        className={`aspect-square rounded-lg bg-cover bg-center border-2 transition-all ${
                          selectedImageIndex === index 
                            ? 'border-pink-600 shadow-md' 
                            : 'border-gray-200 hover:border-pink-300'
                        }`}
                        style={{ backgroundImage: `url(${img})` }}
                      />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">{product.name}</h1>
            <p className="text-lg text-gray-600 mb-6">Product Code: {product.product_code}</p>
            
            <div className="bg-white rounded-lg p-6 mb-6">
              <h2 className="text-xl font-semibold mb-4">Description</h2>
              <p className="text-gray-700 mb-6 whitespace-pre-line">{product.description}</p>
              
              {product.features && product.features.length > 0 && (
                <>
                  <h3 className="font-semibold mb-3 text-lg">Features:</h3>
                  <ul className="space-y-2 mb-6">
                    {product.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {product.specifications && Object.keys(product.specifications).length > 0 && (
                <>
                  <h3 className="font-semibold mb-3 text-lg border-t pt-4">Specifications:</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                    {Object.entries(product.specifications).map(([key, value]) => (
                      <div key={key} className="flex gap-2">
                        <span className="font-medium text-gray-600">{key}:</span>
                        <span className="text-gray-900">{value}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}

              <div className="border-t pt-4 mt-4">
                <p className="text-sm text-gray-600">
                  <strong>Category:</strong> {product.category}
                </p>
                {product.subcategory && (
                  <p className="text-sm text-gray-600 mt-1">
                    <strong>Subcategory:</strong> {product.subcategory}
                  </p>
                )}
              </div>
            </div>

            <div className="bg-white rounded-lg p-6 mb-6">
              <h3 className="font-semibold mb-4">Select Quantity</h3>
              <div className="flex items-center gap-4 mb-6">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setQuantity(Math.max(1, quantity - 10))}
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <input
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-24 text-center border rounded-md px-3 py-2 text-lg font-semibold"
                  min={1}
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setQuantity(quantity + 10)}
                >
                  <Plus className="h-4 w-4" />
                </Button>
                <span className="text-sm text-gray-600">units</span>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={handleAddToQuote}
                  variant="outline"
                  className="flex-1"
                >
                  Add to Quote
                </Button>
                <Button
                  onClick={handleRequestQuoteNow}
                  className="flex-1"
                >
                  Request Quote Now
                </Button>
              </div>
            </div>

            <div className="bg-pink-50 border border-pink-200 rounded-lg p-4">
              <p className="text-sm text-pink-900">
                <strong>Need help?</strong> Contact our team at <strong>1300 722 659</strong> or <Link to="/contact" className="text-pink-600 hover:underline">request a callback</Link>.
              </p>
            </div>
          </div>
        </div>

        {relatedProducts.length > 0 && (
          <div>
            <h2 className="text-3xl font-bold mb-8">Related Products</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProducts.map((relProduct) => (
                <Link key={relProduct.product_code} to={`/product/${relProduct.product_code}`}>
                  <Card className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    <CardContent className="p-6">
                      <ProductImage
                        src={relProduct.local_image ? `https://procloudpromo.com${relProduct.local_image}` : relProduct.image_url}
                        alt={relProduct.name}
                        className="aspect-square rounded-lg overflow-hidden mb-4"
                      />
                      <h3 className="font-semibold mb-2 group-hover:text-pink-600 transition-colors">
                        {relProduct.name}
                      </h3>
                      <p className="text-sm text-gray-600">{relProduct.product_code}</p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;
